import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { PasswordChangeRequiredError, authService } from '../services/authService';
import { AuthLegalFooter } from './AuthLegalFooter';
import {
  AuthAlert,
  AuthField,
  AuthInput,
  AuthPageLayout,
  AuthSubmitButton,
  authStyles,
} from './AuthPageLayout';
import { resolveReturnPath, stripEmptyReturnUrlSearch } from '../utils/loginReturnUrl';

const ssoErrorMessage = (code: string | null, t: (key: string) => string): string | null => {
  if (!code) {
    return null;
  }

  const key = `errors.${code}`;
  const translated = t(key);
  return translated === key ? t('errors.ssoFailed') : translated;
};

export const LoginPage: React.FC = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [ssoConfigured, setSsoConfigured] = React.useState(false);
  const [ssoEnabled, setSsoEnabled] = React.useState(false);

  const returnPath = React.useMemo(
    () => resolveReturnPath(location.search),
    [location.search],
  );

  React.useEffect(() => {
    const cleanedSearch = stripEmptyReturnUrlSearch(location.search);
    if (cleanedSearch === null) {
      return;
    }

    navigate({ pathname: '/login', search: cleanedSearch }, { replace: true });
  }, [location.search, navigate]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ssoError = ssoErrorMessage(params.get('ssoError'), t);
    if (ssoError) {
      setError(ssoError);
    }
  }, [location.search, t]);

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate(returnPath, { replace: true });
    }
  }, [isAuthenticated, navigate, returnPath]);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const status = await authService.getSsoStatus();
      if (!cancelled) {
        setSsoConfigured(status.configured);
        setSsoEnabled(status.enabled);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!username.trim()) {
      setError(t('errors.usernameRequired'));
      return;
    }

    if (ssoEnabled) {
      setIsSubmitting(true);
      try {
        window.location.assign(await authService.startSso(username.trim(), returnPath));
      } catch (err) {
        setError(err instanceof Error ? err.message : t('errors.unableToLogin'));
        setIsSubmitting(false);
      }
      return;
    }

    if (!password) {
      setError(t('errors.passwordRequired'));
      return;
    }

    setIsSubmitting(true);
    try {
      await login(username.trim(), password);
      navigate(returnPath, { replace: true });
    } catch (err) {
      if (err instanceof PasswordChangeRequiredError) {
        const query = new URLSearchParams({ required: '1', username: username.trim() });
        navigate(`/reset-password?${query.toString()}`, { replace: true });
        return;
      }

      setError(err instanceof Error ? err.message : t('errors.unableToLogin'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onContinueWithSso = async () => {
    setError(null);
    if (!username.trim()) {
      setError(t('errors.usernameRequired'));
      return;
    }

    setIsSubmitting(true);
    try {
      window.location.assign(await authService.startSso(username.trim(), returnPath));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.unableToLogin'));
      setIsSubmitting(false);
    }
  };

  return (
    <AuthPageLayout title={t('signInTitle')} subtitle={t('signInSubtitle')} footer={<AuthLegalFooter />}>
      <form className={authStyles.form} onSubmit={onSubmit}>
        {error ? <AuthAlert variant="error">{error}</AuthAlert> : null}

        <AuthField label={t('emailLabel')} htmlFor="login-email" icon="mail">
          <AuthInput
            id="login-email"
            value={username}
            onChange={e => setUsername(e.target.value)}
            autoComplete="username"
            placeholder={t('emailPlaceholder')}
          />
        </AuthField>

        {ssoEnabled ? null : (
          <AuthField label={t('passwordLabel')} htmlFor="login-password" icon="lock">
            <AuthInput
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              placeholder={t('passwordPlaceholder')}
              withToggle
              revealed={showPassword}
              onToggleReveal={() => setShowPassword(current => !current)}
              toggleLabel={showPassword ? t('hidePassword') : t('showPassword')}
            />
          </AuthField>
        )}

        {ssoEnabled ? null : (
          <div className={authStyles.formFooter}>
            <span />
            <Link className={authStyles.link} to="/forgot-password">
              {t('forgotPassword')}
            </Link>
          </div>
        )}

        <AuthSubmitButton loading={isSubmitting} loadingLabel={ssoEnabled ? t('ssoRedirecting') : t('signingIn')}>
          {ssoEnabled ? t('continueWithSso') : t('signIn')}
        </AuthSubmitButton>

        {ssoConfigured && !ssoEnabled ? (
          <button type="button" className={authStyles.link} style={{ marginTop: 12 }} onClick={() => void onContinueWithSso()} disabled={isSubmitting}>
            {t('continueWithSso')}
          </button>
        ) : null}
      </form>
    </AuthPageLayout>
  );
};

export default LoginPage;
